import { Alert, Box, Link, Typography } from '@mui/material';
import { useEmailVerificationResend } from '../../hooks/useEmailVerificationResend';
import TurnstileChallenge from '../shared/TurnstileChallenge';
import useLocalization from '../../hooks/useLocalization';

import { mergeSx } from '../../utils/styles';

const styles = {
  wrapper: {
    padding: 5,
  },
  header: {
    typography: 'h3',
    textAlign: 'center',
    marginBottom: 4,
  },
  paragraph: {
    color: 'neutral.100',
    typography: 'body3',
    marginBottom: 2,
    position: 'relative',
  },
  link: {
    color: 'info.main',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
  disabledLink: {
    color: 'neutral.400',
    textDecoration: 'none',
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  resend: {
    position: 'absolute',
    marginBottom: 0,
    left: '105%',
    top: '0%',
    whiteSpace: 'nowrap',
    typography: 'body3',
  },
  container: {
    position: 'relative',
  },
};

const EmailVerification = ({ email, sendOnOpen = false }: { email?: string; sendOnOpen?: boolean }) => {
  const { t } = useLocalization();
  const { turnstile, isDisabled, countdown, resend, requestError } = useEmailVerificationResend(email, sendOnOpen);

  return (
    <Box sx={styles.wrapper}>
      <Typography sx={styles.header}>{t('register.emailVerificationHeader')}</Typography>
      <Typography component='p' sx={styles.paragraph}>
        {t('register.emailVerificationMessage', { email: email })}
      </Typography>
      <Typography component='p' sx={styles.paragraph}>
        {t('register.noVerificationCode')}
      </Typography>
      <TurnstileChallenge {...turnstile.widgetProps} />
      {requestError && (
        <Alert severity='error'>
          {requestError === 'unexpectedError' ? t('unexpectedError') : t(`turnstile.${requestError}`)}
        </Alert>
      )}
      <Box
        sx={mergeSx(
          {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          },
          styles.container
        )}
      >
        <Typography sx={styles.paragraph}>
          <Link
            component='button'
            type='button'
            disabled={isDisabled}
            onClick={() => void resend()}
            sx={isDisabled ? styles.disabledLink : styles.link}
          >
            {countdown > 0
              ? `${t('register.clickHere')} (${countdown}${t('register.seconds')})`
              : t('register.clickHere')}
          </Link>{' '}
          <Typography sx={styles.resend}>{t('register.toResendEmail')}</Typography>
        </Typography>
      </Box>
    </Box>
  );
};

export default EmailVerification;
